import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { isAxiosError } from 'axios';
import { safeLocalStorage } from '@/lib/safe-storage';
import type {
  City, 
  DeliveryPoint, 
  DeliveryZone, 
  AddressSuggestion,
  DeliveryCheckResult,
} from '@/lib/api/delivery';
import { deliveryApi } from '@/lib/api/delivery';

// ========== TYPES ==========

export interface SelectedAddress {
  address: string;
  coordinates?: {
    lat: number;
    lon: number;
  };
  deliveryPointId?: number;
  deliveryZone?: DeliveryZone;
  deliveryCost?: number;
  estimatedTime?: string;
  minOrderSum?: number;
  isInZone: boolean;
}

/** Зачем открыли выбор адреса — от этого зависит, куда вернуть человека. */
export type AddressSheetIntent = 'none' | 'checkout';

/**
 * Проверить адрес не получилось — это НЕ «вне зоны».
 * Бэк отдаёт такой исход с HTTP 200 и полем `error`
 * (controllers/deliveryController.js: координаты не определились),
 * поэтому отличать его надо явно, иначе жителю зоны напишут, что мы к нему
 * не ездим.
 */
export const ADDRESS_CHECK_FAILED_MESSAGE = 'Не удалось проверить адрес';

type AddressCheckFailReason = 'geocode_failed' | 'suggest_failed' | 'city_missing' | 'network';

// Сбой проверки адреса приходит с HTTP 200 — ни fetch-, ни XHR-перехватчик
// error-tracker'а его не увидит. Поэтому шлём отдельное продуктовое событие.
function trackAddressCheckFailed(
  reason: AddressCheckFailReason,
  query: string,
  httpStatus: number | null,
  hasPrevAddress: boolean
): void {
  import('@/lib/source-tracker').then(({ captureEvent }) => {
    captureEvent('address_check_failed', {
      reason,
      http_status: httpStatus,
      query,
      has_prev_address: hasPrevAddress,
    });
  }).catch(() => { /* ignore */ });
}

interface AddressState {
  // UI State
  isSheetOpen: boolean;
  sheetIntent: AddressSheetIntent;
  /**
   * Человек упёрся в гейт адреса. Раньше это был тост, который улетал за
   * 4 секунды и не объяснял, зачем адрес нужен. Теперь — отдельное окно
   * с объяснением и одной понятной кнопкой.
   */
  requiredPromptSource: 'checkout' | 'mini_cart' | null;
  /** Одноразовый сигнал «адрес подтверждён — вернуть человека в корзину». */
  resumeCheckout: boolean;
  isLoading: boolean;
  /** Проверку адреса не смогли выполнить (геокодер/сеть/город) — не «вне зоны». */
  addressCheckError: string | null;
  /** Подсказки адреса не отвечают: пустой список тут не значит «не найдено». */
  suggestError: boolean;

  // Hydration state for SSR
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
  
  // Selected Data
  selectedCity: City | null;
  selectedAddress: SelectedAddress | null;
  
  // Data
  cities: City[];
  deliveryPoints: DeliveryPoint[];
  suggestions: AddressSuggestion[];
  
  // Delivery check result
  lastCheckResult: DeliveryCheckResult | null;
  
  // Actions - UI
  /**
   * intent='checkout' — адрес спрашивают, потому что человек шёл в корзину.
   * После подтверждения его надо туда вернуть; после отмены — не открывать.
   */
  openSheet: (intent?: AddressSheetIntent) => void;
  /** Подтверждение адреса кнопкой. Только отсюда взводится resumeCheckout. */
  confirmSheet: () => void;
  closeSheet: () => void;
  /** Показать окно «нужен адрес» вместо тоста. */
  openRequiredPrompt: (source: 'checkout' | 'mini_cart') => void;
  closeRequiredPrompt: () => void;
  consumeResumeCheckout: () => void;
  
  // Actions - Cities
  fetchCities: () => Promise<void>;
  setSelectedCity: (city: City | null) => void;
  
  // Actions - Delivery Points
  fetchDeliveryPoints: (cityId: number) => Promise<void>;
  
  // Actions - Address
  setSelectedAddress: (address: SelectedAddress | null) => void;
  checkAddress: (address: string, coordinates?: { lat: number; lon: number }) => Promise<DeliveryCheckResult>;
  clearAddressCheckError: () => void;
  clearAddress: () => void;
  
  // Actions - Autocomplete
  fetchSuggestions: (query: string) => Promise<void>;
  clearSuggestions: () => void;
  selectSuggestion: (suggestion: AddressSuggestion) => Promise<void>;
}

// ========== STORE ==========

export const useAddressStore = create<AddressState>()(
  persist(
    immer((set, get) => ({
      // Initial State
      isSheetOpen: false,
      sheetIntent: 'none',
      requiredPromptSource: null,
      resumeCheckout: false,
      isLoading: false,
      addressCheckError: null,
      suggestError: false,
      selectedCity: null,
      selectedAddress: null,
      cities: [],
      deliveryPoints: [],
      suggestions: [],
      lastCheckResult: null,
      
      // Hydration state
      _hasHydrated: false,
      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },

      // UI Actions
      openSheet: (intent = 'none') => {
        set((state) => {
          state.isSheetOpen = true;
          state.sheetIntent = intent;
          state.resumeCheckout = false;
          // Открывая карту, гасим окно-объяснение: два слоя поверх друг друга
          // не нужны, объяснение своё дело уже сделало.
          state.requiredPromptSource = null;
        });
      },

      openRequiredPrompt: (source) => {
        set((state) => {
          state.requiredPromptSource = source;
        });
      },

      closeRequiredPrompt: () => {
        set((state) => {
          state.requiredPromptSource = null;
        });
      },

      // Вызывается ТОЛЬКО кнопкой подтверждения при адресе в зоне.
      // Крестик, оверлей и Esc идут в closeSheet и корзину не открывают —
      // иначе гейт превратился бы в ловушку: закрыл выбор адреса, а тебя
      // всё равно втащило в корзину.
      confirmSheet: () => {
        set((state) => {
          state.isSheetOpen = false;
          state.suggestions = [];
          state.resumeCheckout = state.sheetIntent === 'checkout';
          state.sheetIntent = 'none';
        });
      },

      closeSheet: () => {
        set((state) => {
          state.isSheetOpen = false;
          state.suggestions = [];
          state.sheetIntent = 'none';
          state.resumeCheckout = false;
        });
      },

      consumeResumeCheckout: () => {
        set((state) => {
          state.resumeCheckout = false;
        });
      },

      // Cities Actions
      fetchCities: async () => {
        try {
          set((state) => { state.isLoading = true; });
          const cities = await deliveryApi.getCities();
          set((state) => {
            state.cities = cities;
            // Auto-select first city if none selected
            if (!state.selectedCity && cities.length > 0) {
              state.selectedCity = cities[0];
            }
            state.isLoading = false;
          });
          
          // Fetch delivery points for selected city
          const { selectedCity, fetchDeliveryPoints } = get();
          if (selectedCity) {
            await fetchDeliveryPoints(selectedCity.id);
          }
        } catch (error) {
          console.error('Failed to fetch cities:', error);
          set((state) => { state.isLoading = false; });
        }
      },

      setSelectedCity: (city) => {
        set((state) => {
          state.selectedCity = city;
          state.deliveryPoints = [];
          state.selectedAddress = null;
          state.suggestions = [];
        });
        
        if (city) {
          get().fetchDeliveryPoints(city.id);
        }
      },

      // Delivery Points Actions
      fetchDeliveryPoints: async (cityId) => {
        try {
          const points = await deliveryApi.getDeliveryPoints(cityId);
          set((state) => {
            state.deliveryPoints = points;
          });
        } catch (error) {
          console.error('Failed to fetch delivery points:', error);
        }
      },

      // Address Actions
      setSelectedAddress: (address) => {
        set((state) => {
          state.selectedAddress = address;
        });
      },

      checkAddress: async (address, coordinates) => {
        const hadPrevAddress = !!get().selectedAddress;
        const { selectedCity } = get();
        if (!selectedCity) {
          // Города нет — не ответил /cities. Раньше выходили молча, и клик по
          // подсказке не давал вообще ничего. Показываем сбой и повторяем
          // загрузку городов, чтобы следующая попытка сработала.
          set((state) => {
            state.isLoading = false;
            state.addressCheckError = ADDRESS_CHECK_FAILED_MESSAGE;
          });
          trackAddressCheckFailed('city_missing', address, null, hadPrevAddress);
          void get().fetchCities();
          return { isInZone: false, error: 'Город не выбран' };
        }

        try {
          set((state) => {
            state.isLoading = true;
            state.addressCheckError = null;
          });
          const result = await deliveryApi.checkDeliveryZone(address, selectedCity.id, coordinates);

          set((state) => {
            state.lastCheckResult = result;

            if (result.isInZone) {
              state.selectedAddress = {
                address,
                isInZone: true,
                deliveryPointId: result.deliveryPointId,
                deliveryZone: result.zone,
                deliveryCost: result.deliveryCost,
                estimatedTime: result.estimatedTime,
                minOrderSum: result.minOrderSum,
              };
              state.addressCheckError = null;
            } else if (result.error) {
              // HTTP 200 + error = координаты не определились. Это не отказ в
              // доставке: прошлый адрес не трогаем, человеку показываем повтор.
              state.addressCheckError = ADDRESS_CHECK_FAILED_MESSAGE;
            } else {
              state.selectedAddress = {
                address,
                isInZone: false,
              };
              state.addressCheckError = null;
            }

            state.isLoading = false;
          });

          if (!result.isInZone && result.error) {
            trackAddressCheckFailed('geocode_failed', address, 200, hadPrevAddress);
          }

          return result;
        } catch (error) {
          console.error('Failed to check address:', error);
          const status = isAxiosError(error) ? error.response?.status ?? null : null;
          set((state) => {
            state.isLoading = false;
            state.addressCheckError = ADDRESS_CHECK_FAILED_MESSAGE;
          });
          trackAddressCheckFailed('network', address, status, hadPrevAddress);
          return { isInZone: false, error: 'Ошибка проверки адреса' };
        }
      },

      clearAddressCheckError: () => {
        set((state) => {
          state.addressCheckError = null;
        });
      },

      clearAddress: () => {
        set((state) => {
          state.selectedAddress = null;
          state.lastCheckResult = null;
          state.suggestions = [];
          state.addressCheckError = null;
        });
      },

      // Autocomplete Actions
      fetchSuggestions: async (query) => {
        if (query.length < 3) {
          set((state) => { state.suggestions = []; state.suggestError = false; });
          return;
        }

        const { selectedCity } = get();

        try {
          const result = await deliveryApi.autocomplete(query, selectedCity?.name);
          set((state) => {
            state.suggestions = result.suggestions || [];
            state.suggestError = false;
          });
        } catch (error) {
          console.error('Failed to fetch suggestions:', error);
          // Пустой список после сбоя нельзя показывать как «адрес не найден» —
          // человек уходит уверенным, что мы к нему не ездим.
          set((state) => { state.suggestions = []; state.suggestError = true; });
          const status = isAxiosError(error) ? error.response?.status ?? null : null;
          trackAddressCheckFailed('suggest_failed', query, status, !!get().selectedAddress);
        }
      },

      clearSuggestions: () => {
        set((state) => {
          state.suggestions = [];
          state.suggestError = false;
        });
      },

      selectSuggestion: async (suggestion) => {
        const address = suggestion.value;

        // Validate that address has house number
        if (!suggestion.data.house) {
          set((state) => {
            state.suggestions = [];
            state.selectedAddress = null;
          });
          return;
        }

        // Set coordinates if available
        const coordinates = suggestion.data.geo_lat && suggestion.data.geo_lon
          ? { lat: parseFloat(suggestion.data.geo_lat), lon: parseFloat(suggestion.data.geo_lon) }
          : undefined;

        set((state) => {
          state.suggestions = [];
        });

        // Check delivery zone — передаём координаты DaData чтобы не геокодировать повторно
        await get().checkAddress(address, coordinates);

        // Update coordinates
        // Дописываем только тому адресу, для которого зона реально считалась:
        // при сбое проверки в сторе лежит ПРОШЛЫЙ адрес, и координаты нового
        // дома уехали бы к нему.
        if (coordinates) {
          set((state) => {
            if (state.selectedAddress && state.selectedAddress.address === address) {
              state.selectedAddress.coordinates = coordinates;
            }
          });
        }
      },
    })),
    {
      name: 'aziatochka-address',
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (state) => ({
        selectedCity: state.selectedCity,
        selectedAddress: state.selectedAddress,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true);
        }
      },
    }
  )
);

// ========== SELECTORS ==========

export const selectSelectedAddress = (state: AddressState) => state.selectedAddress;
export const selectSelectedCity = (state: AddressState) => state.selectedCity;
export const selectIsAddressSheetOpen = (state: AddressState) => state.isSheetOpen;
export const selectDeliveryCost = (state: AddressState) => state.selectedAddress?.deliveryCost ?? 0;
export const selectMinOrderSum = (state: AddressState) => state.selectedAddress?.minOrderSum ?? 0;
export const selectIsInDeliveryZone = (state: AddressState) => state.selectedAddress?.isInZone ?? false;
export const selectAddressHasHydrated = (state: AddressState) => state._hasHydrated;
